/**
 * Canonical outer ring for map/turf: GeoJSON order [longitude, latitude] per vertex.
 */
export type NormalizedGeoRing = {
  ringLonLat: number[][];
};

function pairToLonLat(pair: number[]): [number, number] {
  const [a, b] = pair;
  // Central Europe typical: lat ~47–55 first, lon ~5–15 second (e.g. [50.38, 8.06])
  if (a > 35 && a < 60 && b > -20 && b < 35) {
    return [b, a];
  }
  // GeoJSON order: lon ~5–15 first, lat ~47–55 second (e.g. [8.06, 50.38])
  if (b > 35 && b < 60 && a > -20 && a < 35) {
    return [a, b];
  }
  return [a, b];
}

function ringFromGeometry(geometry: any): number[][] | null {
  if (!geometry) {
    return null;
  }
  if (geometry.type === "Polygon" && Array.isArray(geometry.coordinates?.[0])) {
    return geometry.coordinates[0].map((c: number[]) => [c[0], c[1]]);
  }
  if (geometry.type === "MultiPolygon" && Array.isArray(geometry.coordinates?.[0]?.[0])) {
    return geometry.coordinates[0][0].map((c: number[]) => [c[0], c[1]]);
  }
  return null;
}

function ringFromShapeValue(shape: unknown): number[][] | null {
  if (shape == null) {
    return null;
  }
  let value: any = shape;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (
      Array.isArray(first) &&
      first.length === 2 &&
      typeof first[0] === "number" &&
      typeof first[1] === "number"
    ) {
      return (value as number[][]).map((p) => pairToLonLat(p));
    }
    if (first && typeof first === "object" && !Array.isArray(first)) {
      return (value as any[]).map((p: any) => [
        p.lon ?? p.longitude ?? p.lng,
        p.lat ?? p.latitude,
      ]);
    }
  }
  if (value?.type === "Polygon" && Array.isArray(value.coordinates?.[0])) {
    return value.coordinates[0].map((c: number[]) => [c[0], c[1]]);
  }
  if (value?.type === "MultiPolygon" && Array.isArray(value.coordinates?.[0]?.[0])) {
    return value.coordinates[0][0].map((c: number[]) => [c[0], c[1]]);
  }
  return null;
}

/**
 * Reads optional `geometry` and `shape` (as on Building / parcel-like documents) and returns
 * one outer ring in canonical [lon, lat] order, or null if none could be parsed.
 */
export function normalizeGeoShape(
  source: { shape?: unknown; geometry?: unknown } | null | undefined,
): NormalizedGeoRing | null {
  if (!source) {
    return null;
  }
  const fromGeom = ringFromGeometry(source.geometry);
  if (fromGeom && fromGeom.length >= 3) {
    return { ringLonLat: fromGeom };
  }
  const fromShape = ringFromShapeValue(source.shape);
  if (fromShape && fromShape.length >= 3) {
    return { ringLonLat: fromShape };
  }
  return null;
}

/**
 * Outer ring [lon, lat][] or null — avoids optional-chaining + property access in TSX (parser quirks).
 */
export function ringLonLatFromGeoSource(
  source: { shape?: unknown; geometry?: unknown } | null | undefined,
): number[][] | null {
  const n = normalizeGeoShape(source);
  return n ? n.ringLonLat : null;
}

/** GeoJSON rings should be closed; turf fill areas expect closed rings. */
export function ensureClosedRing(ring: number[][]): number[][] {
  if (ring.length < 3) {
    return ring;
  }
  const a = ring[0];
  const b = ring[ring.length - 1];
  if (a[0] === b[0] && a[1] === b[1]) {
    return ring;
  }
  return [...ring, [a[0], a[1]]];
}

/**
 * The building's outline as a GeoJSON polygon, or null where it has none.
 *
 * The same shape the overview map draws in red and the parcel map draws underneath the plot. Shared
 * because two readings of a building's geometry are two chances for the outline on one map to sit
 * somewhere else than on the other.
 */
export const footprintPolygon = (building: any): any | null => {
    const ring = ringLonLatFromGeoSource(building);
    if (!ring || ring.length < 3) return null;

    return { type: 'Polygon', coordinates: [ensureClosedRing(ring)] };
};
